import { Injectable } from "@angular/core";
import { ModalType } from "@components/modal/modal.component";
import { ModalFormValues, ModalInput, ModalSection } from "@interfaces/modal";
import { Store } from "@ngrx/store";
import { AppActions } from "@state/actions/app.actions";
import { v4 } from "uuid";

@Injectable({
  providedIn: "root"
})
export class ModalService {
  constructor(private readonly store: Store) {}

  /**
   * `confirm`/`options` permitem oferecer uma saída a partir do erro (ex.:
   * "Ir para o campo" numa falha de emissão), com um botão neutro para apenas
   * fechar. Sem eles o modal continua sendo o "Erro" com um botão só.
   */
  error(
    message: string | string[],
    confirm?: () => void,
    options?: {
      title?: string;
      confirmText?: string;
      neutralText?: string;
    }
  ) {
    this._build(
      ModalType.ERROR,
      options?.title ?? "Erro",
      message,
      confirm,
      undefined,
      {
        confirmText: options?.confirmText,
        neutralText: options?.neutralText
      }
    );
  }

  success(message?: string, confirm?: () => void) {
    this._build(
      ModalType.SUCCESS,
      "Sucesso",
      message ?? "Operação realizada com sucesso!",
      confirm
    );
  }

  info(
    message: string,
    title: string,
    confirm?: () => void,
    cancel?: () => void
  ) {
    this._build(ModalType.INFO, title, message, confirm, cancel);
  }

  /**
   * `options` permite usar o modal como uma escolha entre duas ações (ex.:
   * "Salvar rascunho" x "Descartar nota"), e não só como um sim/não.
   */
  confirm(
    message: string,
    confirm?: () => void,
    cancel?: () => void,
    options?: {
      title?: string;
      confirmText?: string;
      cancelText?: string;
      cancelColor?: string;
      /** Terceira opção, neutra: fecha o modal sem decidir nada. */
      neutralText?: string;
      neutral?: () => void;
      /** Resumo para conferência antes de confirmar. */
      sections?: ModalSection[];
    }
  ) {
    this._build(
      ModalType.CONFIRM,
      options?.title ?? "Tem certeza?",
      message,
      confirm,
      cancel,
      {
        confirmText: options?.confirmText,
        cancelText: options?.cancelText,
        cancelColor: options?.cancelColor,
        neutralText: options?.neutralText,
        neutral: options?.neutral,
        sections: options?.sections
      }
    );
  }

  deletion(
    title?: string,
    message?: string,
    confirm?: (input?: string) => void,
    cancel?: (input?: string) => void,
    input?: ModalInput
  ) {
    this._build(
      ModalType.DELETION,
      title ?? "Tem certeza?",
      message ?? "",
      confirm,
      cancel,
      {
        input
      }
    );
  }

  input(
    title: string,
    message: string,
    confirm?: (input?: string) => void,
    cancel?: () => void,
    input?: ModalInput
  ) {
    this._build(ModalType.INPUT, title, message, confirm, cancel, {
      input
    });
  }

  inputs(
    title: string,
    message: string,
    confirm?: (values?: ModalFormValues) => void,
    cancel?: () => void,
    inputs: ModalInput[] = []
  ) {
    this._build(ModalType.INPUT, title, message, undefined, cancel, {
      inputs,
      confirmValues: confirm
    });
  }

  private _build(
    type: ModalType,
    title: string,
    message: string | string[],
    confirm?: (input?: string) => void,
    cancel?: () => void,
    options?: {
      input?: ModalInput;
      inputs?: ModalInput[];
      confirmValues?: (values?: ModalFormValues) => void;
      confirmText?: string;
      cancelText?: string;
      cancelColor?: string;
      neutralText?: string;
      neutral?: () => void;
      sections?: ModalSection[];
    }
  ) {
    this.store.dispatch(
      AppActions.queueModal({
        modal: {
          type,
          id: v4(),
          message,
          title,
          confirm,
          confirmValues: options?.confirmValues,
          cancel,
          neutral: options?.neutral,
          confirmText: options?.confirmText,
          cancelText: options?.cancelText,
          neutralText: options?.neutralText,
          cancelColor: options?.cancelColor,
          input: options?.input,
          inputs: options?.inputs,
          sections: options?.sections
        }
      })
    );
  }
}
